import { useMediaPlaybackStore } from "%/stores/media-playback-store";
import { isLegacyUrl, mediaIdFromValue } from "%/lib/media-reference";

// How long to wait before re-checking a media-backed segment that has no playable
// URL yet (still importing/transcoding).
const PLAYBACK_POLL_INTERVAL_MS = 5000;

const MSG_PLAYBACK_UNRESOLVED = "could not resolve playback for a media-backed story segment";

// Story segment types whose content can reference a content media id ({ mediaId })
// instead of a baked URL. Image resolves to the converted WebP read URL; video and
// audio to the HLS master once ready / the source file while in flight.
const MEDIA_SEGMENT_TYPES = ["image", "video", "audio"];

// Facts about a media item (from the same playback lookup) injected into the
// segment so iEditor's destination-constraints report can judge existing content.
// Display-only like `src`: stripped again before save.
const MEDIA_FACT_KEYS = [
  "mediaDurationSeconds",
  "mediaBytes",
  "mediaWidth",
  "mediaHeight",
  "mediaMime",
  "mediaTitle",
  "mediaArtist",
  "mediaAlbum",
  "mediaCoverSrc",
];

// Resolves media-backed story segments ({ mediaId }) — image, video, or audio — to
// a playback URL for display, and injects it as the segment's src so iEditor's
// segment editors (edit) and previews (production) render the real media instead of
// the block's hard-coded placeholder. The injected src is display only:
// stripResolvedVideoSrc() removes it again before the segments are saved, keeping
// { mediaId } as the persisted contract.
//
// The editor is never gated on resolution — it renders immediately and each block
// updates once its URL arrives. The blocks snapshot their `data` on mount, so we
// bump `playbackVersion` (an iEditor :key) to re-render them with the injected src.
// While an item has no source/HLS/image URL yet we poll every 5s until one is
// available.
//
// The host exposes its segment array via `playbackSegments` (defaults to the
// editor's `value` modelValue; a host that surfaces segments under a different
// name overrides it). segment_content arrives as a JSON string in some hosts (the
// store stringifies it for VCL's migrateSegmentData) and an object in others, so
// everything is string/object-safe.
export const StoryVideoPlaybackMixin = {
  data() {
    return {
      // mediaId -> resolved playback URL.
      videoPlaybackUrls: {},
      // mediaId -> its poster URL, resolved server-side alongside the playback URL.
      mediaThumbnailUrls: {},
      // mediaId -> { mediaDurationSeconds, mediaBytes, mediaWidth, mediaHeight, mediaMime }.
      mediaFactsById: {},
      // Bumped whenever a URL resolves; used as the iEditor :key so the video
      // block re-mounts and re-reads the injected src.
      playbackVersion: 0,
      playbackPollTimer: null,
    };
  },
  computed: {
    // Default segment source — the editor's modelValue. Hosts that expose
    // segments under a different name override this computed.
    playbackSegments() {
      return Array.isArray(this.value) ? this.value : [];
    },
    // The segment array to render, with resolved playback URLs injected.
    resolvedSegments() {
      return this.playbackSegments.map(segment => this.applyResolvedVideoSrc(segment));
    },
    hidesUnresolvedMedia() {
      return false;
    },
    renderableSegments() {
      if (!this.hidesUnresolvedMedia) {
        return this.resolvedSegments;
      }
      return this.resolvedSegments.filter(segment => this.hasResolvedMedia(segment));
    },
    videoMediaIds() {
      return this.playbackSegments
          .filter(segment => MEDIA_SEGMENT_TYPES.includes(segment.segment_type))
          .map(segment => this.parseSegmentContent(segment.segment_content))
          .filter(content => content && content.mediaId && !content.src)
          .map(content => content.mediaId);
    },
    // Cover images (audio segments) stored as a media_id, resolved the same way
    // as a segment's main media. Legacy URL covers return null and pass through.
    coverImageMediaIds() {
      return this.playbackSegments
          .map(segment => this.parseSegmentContent(segment.segment_content))
          .map(content => content && mediaIdFromValue(content.coverImage))
          .filter(mediaId => mediaId !== null);
    },
    posterMediaIds() {
      return this.playbackSegments
          .map(segment => this.parseSegmentContent(segment.segment_content))
          .map(content => content && mediaIdFromValue(content.posterMediaId))
          .filter(mediaId => mediaId !== null);
    },
    unresolvableSegmentMediaIds() {
      return [...new Set(this.playbackSegments
          .map(segment => this.parseSegmentContent(segment.segment_content))
          .flatMap(content => content?.unresolvableMediaIds ?? []))];
    },
    playbackMediaIds() {
      const unresolvableMediaIds = new Set(this.unresolvableSegmentMediaIds);
      return [...new Set([
        ...this.videoMediaIds,
        ...this.coverImageMediaIds,
        ...this.posterMediaIds,
      ])].filter(mediaId => !unresolvableMediaIds.has(mediaId));
    },
  },
  watch: {
    playbackMediaIds: {
      immediate: true,
      handler() {
        this.startVideoPlaybackResolution();
      },
    },
  },
  beforeUnmount() {
    this.stopVideoPlaybackPoll();
  },
  methods: {
    // (Re)start resolution for the current media-backed videos.
    startVideoPlaybackResolution() {
      useMediaPlaybackStore().seedUnresolvableMedia(this.unresolvableSegmentMediaIds);
      this.stopVideoPlaybackPoll();
      this.pollVideoPlayback();
    },
    stopVideoPlaybackPoll() {
      if (this.playbackPollTimer) {
        clearTimeout(this.playbackPollTimer);
        this.playbackPollTimer = null;
      }
    },
    // Resolve every media-backed video that doesn't have a URL yet. The resolver
    // returns no URL while a video has neither a source nor an HLS playlist, so we
    // re-poll every 5s until one is available, then stop. A per-video failure
    // leaves that one unresolved (it falls back to the block default).
    pollVideoPlayback() {
      const unresolvedMediaIds = this.playbackMediaIds.filter(mediaId => !this.videoPlaybackUrls[mediaId]);
      if (!unresolvedMediaIds.length) {
        return;
      }
      const mediaPlaybackStore = useMediaPlaybackStore();
      Promise.all(unresolvedMediaIds.map(mediaId => {
        return new Promise(settle => {
          mediaPlaybackStore.resolveMediaPlayback(mediaId, playback => {
            this.applyResolvedPlayback(mediaId, playback);
            settle();
          }, error => {
            // eslint-disable-next-line no-console
            console.error(MSG_PLAYBACK_UNRESOLVED, { mediaId, error });
            settle();
          });
        });
      })).then(() => {
        const stillUnresolved = this.playbackMediaIds.some(mediaId => !this.videoPlaybackUrls[mediaId]);
        if (stillUnresolved) {
          this.playbackPollTimer = setTimeout(() => this.pollVideoPlayback(), PLAYBACK_POLL_INTERVAL_MS);
        }
      });
    },
    hasResolvedMedia(segment) {
      if (!MEDIA_SEGMENT_TYPES.includes(segment.segment_type)) {
        return true;
      }
      const content = this.parseSegmentContent(segment.segment_content);
      if (!content || !content.mediaId) {
        return true;
      }
      return !!content.src;
    },
    applyResolvedPlayback(mediaId, playback) {
      if (!playback) {
        return;
      }
      const facts = {
        mediaDurationSeconds: Number(playback.durationSeconds) || 0,
        mediaBytes: Number(playback.totalBytes) || 0,
        mediaWidth: Number(playback.width) || 0,
        mediaHeight: Number(playback.height) || 0,
        mediaMime: playback.mime || "",
      };
      if (playback.mediaType === "audio") {
        Object.assign(facts, {
          mediaTitle: playback.title || "",
          mediaArtist: playback.artist || "",
          mediaAlbum: playback.album || "",
          mediaCoverSrc: playback.coverUrl || "",
        });
      }
      if (Object.values(facts).some(Boolean) && JSON.stringify(this.mediaFactsById[mediaId]) !== JSON.stringify(facts)) {
        this.mediaFactsById = { ...this.mediaFactsById, [mediaId]: facts };
        this.playbackVersion += 1;
      }
      if (playback.thumbnailUrl && this.mediaThumbnailUrls[mediaId] !== playback.thumbnailUrl) {
        this.mediaThumbnailUrls = { ...this.mediaThumbnailUrls, [mediaId]: playback.thumbnailUrl };
        this.playbackVersion += 1;
      }
      if (playback.url) {
        this.videoPlaybackUrls = { ...this.videoPlaybackUrls, [mediaId]: playback.url };
        this.playbackVersion += 1;
      }
    },
    parseSegmentContent(content) {
      if (typeof content === "string") {
        try {
          return JSON.parse(content);
        } catch {
          return null;
        }
      }
      if (content && typeof content === "object") {
        return content;
      }
      return null;
    },
    // Re-encode in the shape it arrived (string stays a string so iEditor's
    // JSON.parse still works; object stays an object).
    encodeSegmentContent(originalContent, updatedContent) {
      return typeof originalContent === "string" ? JSON.stringify(updatedContent) : updatedContent;
    },
    // After a new poster is saved for a media: drop its cached playback so the
    // next resolution pass picks up the poster URL the server now reports.
    refreshMediaThumbnail(mediaId) {
      const thumbnailUrls = { ...this.mediaThumbnailUrls };
      delete thumbnailUrls[mediaId];
      this.mediaThumbnailUrls = thumbnailUrls;
      const urls = { ...this.videoPlaybackUrls };
      delete urls[mediaId];
      this.videoPlaybackUrls = urls;
      this.startVideoPlaybackResolution();
    },
    resolvePosterUrl(content) {
      const overrideMediaId = mediaIdFromValue(content.posterMediaId);
      if (overrideMediaId !== null) {
        return this.videoPlaybackUrls[overrideMediaId] || null;
      }
      if (isLegacyUrl(content.poster)) {
        return null;
      }
      return this.mediaThumbnailUrls[content.mediaId] || null;
    },
    applyResolvedVideoSrc(segment) {
      if (!MEDIA_SEGMENT_TYPES.includes(segment.segment_type)) {
        return segment;
      }
      const content = this.parseSegmentContent(segment.segment_content);
      if (!content) {
        return segment;
      }
      let updated = content;
      // Segment's main media: inject the resolved playback URL as a transient src.
      if (content.mediaId) {
        const resolvedUrl = this.videoPlaybackUrls[content.mediaId];
        if (resolvedUrl) {
          updated = { ...updated, src: resolvedUrl };
        }
        const facts = this.mediaFactsById[content.mediaId];
        if (facts) {
          // Image blocks declare no duration; an undeclared key is dropped loudly by VCL.
          const applicable = segment.segment_type === "image"
            ? Object.fromEntries(Object.entries(facts).filter(([key]) => key !== "mediaDurationSeconds"))
            : facts;
          updated = { ...updated, ...applicable };
        }
        const posterUrl = this.resolvePosterUrl(content);
        if (posterUrl) {
          updated = { ...updated, posterSrc: posterUrl };
        }
      }
      // Cover image (audio): inject the resolved URL as a transient coverImageSrc;
      // coverImage stays the persisted media_id. Legacy URL covers are left alone.
      const coverImageMediaId = mediaIdFromValue(content.coverImage);
      if (coverImageMediaId !== null) {
        const resolvedCover = this.videoPlaybackUrls[coverImageMediaId];
        if (resolvedCover) {
          updated = { ...updated, coverImageSrc: resolvedCover };
        }
      }
      if (updated === content) {
        return segment;
      }
      return {
        ...segment,
        segment_content: this.encodeSegmentContent(segment.segment_content, updated),
      };
    },
    // Drop the injected display src from media-backed segments before persisting.
    stripResolvedVideoSrc(segments) {
      return segments.map(segment => {
        if (!MEDIA_SEGMENT_TYPES.includes(segment.segment_type)) {
          return segment;
        }
        const content = this.parseSegmentContent(segment.segment_content);
        if (!content) {
          return segment;
        }
        const hasInjectedSrc = content.src !== undefined
          || content.coverImageSrc !== undefined
          || content.posterSrc !== undefined
          || content.unresolvableMediaIds !== undefined
          || MEDIA_FACT_KEYS.some(key => content[key] !== undefined);
        if (!hasInjectedSrc) {
          return segment;
        }
        const cleanedContent = { ...content };
        delete cleanedContent.src;
        delete cleanedContent.coverImageSrc;
        delete cleanedContent.posterSrc;
        delete cleanedContent.unresolvableMediaIds;
        MEDIA_FACT_KEYS.forEach(key => delete cleanedContent[key]);
        return {
          ...segment,
          segment_content: this.encodeSegmentContent(segment.segment_content, cleanedContent),
        };
      });
    },
  },
};
